import useSWR, { type SWRConfiguration } from "swr"

/**
 * Options for the shared `useSwr` hook.
 */
export type Options = {
    /**
     * Whether to send the request on mount.
     *
     * @default true
     */
    onLoad?: boolean
    /**
     * Override the default fetcher. The default does a GET against `/api{path}` and
     * unwraps the POC `{ data }` envelope; pass a custom fetcher for POST, credentials,
     * or non-enveloped responses. The fetcher receives the SWR key (`/api{path}`).
     */
    fetcher?: (key: string) => Promise<unknown>
    /**
     * Extra SWR configuration (e.g. `revalidateOnFocus`, `keepPreviousData`,
     * `errorRetryCount`). Merged after `revalidateOnMount`.
     */
    config?: SWRConfiguration
}

/**
 * Default fetcher for POC GET endpoints that wrap their payload in a `{ data }` envelope.
 */
const apiFetcher = async (url: string): Promise<unknown> => {
    const response = await fetch(url)

    return (await response.json()).data
}

/** The SWR cache key for a POC API path. */
const buildSwrKey = (path: string) => "/api" + path

/**
 * Shared SWR wrapper for the POC API.
 *
 * Defaults to a GET against `/api{path}` that unwraps the `{ data }` envelope; override
 * `options.fetcher` / `options.config` for anything else (POST, `credentials`, custom
 * revalidation). Returns SWR's response **directly** — no `useState`/`useEffect`
 * mirroring — so `data`, `isValidating` and optimistic `mutate()` stay live.
 *
 * @param path Path to the API endpoint. The `/api` prefix is added for you.
 * @param options Hook options.
 *
 * @template Data The type of the data returned by the fetcher.
 */
export const useSwr = <Data>(path: string, options: Options = {}) => {
    const { onLoad = true, fetcher, config } = options

    return useSWR<Data>(
        buildSwrKey(path),
        (fetcher ?? apiFetcher) as (key: string) => Promise<Data>,
        { revalidateOnMount: onLoad, ...config }
    )
}
